import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Users, CreditCard, Check, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { locationsAPI, bookingsAPI } from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

const Booking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(true);
  
  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [guests, setGuests] = useState(2);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [cars, setCars] = useState(1);
  const [tents, setTents] = useState(1);
  const [cabinPersons, setCabinPersons] = useState(0);
  const [showCheckInCal, setShowCheckInCal] = useState(false);
  const [showCheckOutCal, setShowCheckOutCal] = useState(false);
  const [busyDates, setBusyDates] = useState([]); // ['YYYY-MM-DD', ...]

  // Helper: is the given Date one of the busy dates? (compared by yyyy-mm-dd)
  const isDateBusy = (date) => {
    if (!date) return false;
    const iso = format(date, 'yyyy-MM-dd');
    return busyDates.includes(iso);
  };
  const busyDateObjects = busyDates.map(s => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  });

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        setLoadingLocation(true);
        const response = await locationsAPI.getById(id);
        setLocation(response.data);
      } catch (error) {
        console.error('Error fetching location:', error);
        setLocation(null);
      } finally {
        setLoadingLocation(false);
      }
    };
    fetchLocation();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    locationsAPI.getBusyDates(id)
      .then(r => setBusyDates(r.data?.busy_dates || []))
      .catch(() => setBusyDates([]));
  }, [id]);

  if (loadingLocation) {
    return (
      <div className="min-h-screen">
        <div className="relative"><Header /></div>
        <div className="flex items-center justify-center py-20">
          <div className="text-center bg-white/90 p-8 rounded-xl">
            <p className="text-xl">Načítání…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="min-h-screen">
        <div className="relative"><Header /></div>
        <div className="flex items-center justify-center py-20">
          <div className="text-center bg-white/90 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-4">Místo nenalezeno</h2>
            <Button onClick={() => navigate('/locations')}>Zpět na místa</Button>
          </div>
        </div>
      </div>
    );
  }

  const maxCapacity = location.max_capacity || location.maxCapacity || 6;
  const isPerUnit = location.pricing_mode === 'per_unit';

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const nights = calculateNights();

  const calcTotal = () => {
    if (isPerUnit) {
      // If guest opts for cabin/maringotka, adults & children pricing is replaced
      // by `unit + cabinPersons × per_person`. Cars & tents still apply.
      const cabinActive = (cabinPersons || 0) > 0 && ((location.price_per_cabin || 0) > 0 || (location.price_per_cabin_unit || 0) > 0);
      const personsPart = cabinActive
        ? (location.price_per_cabin_unit || 0) + (cabinPersons || 0) * (location.price_per_cabin || 0)
        : (adults || 0) * (location.price_per_adult || 0) +
          (children || 0) * (location.price_per_child || 0);
      const perNight =
        personsPart +
        (cars || 0) * (location.price_per_car || 0) +
        (tents || 0) * (location.price_per_tent || 0);
      return Math.round(perNight * nights);
    }
    return Math.round((location.price || 0) * nights);
  };

  const totalPrice = calcTotal();
  const cabinActive = isPerUnit && (cabinPersons || 0) > 0 && ((location.price_per_cabin || 0) > 0 || (location.price_per_cabin_unit || 0) > 0);
  const effectiveGuests = isPerUnit
    ? (cabinActive ? (cabinPersons || 0) : (adults || 0) + (children || 0))
    : guests;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!checkIn || !checkOut) {
      toast.error('Vyberte datum příjezdu a odjezdu');
      return;
    }

    if (checkOut <= checkIn) {
      toast.error('Datum odjezdu musí být po datu příjezdu');
      return;
    }

    if (effectiveGuests > maxCapacity) {
      toast.error(`Maximální počet osob je ${maxCapacity}`);
      return;
    }

    if (effectiveGuests < 1) {
      toast.error('Zadejte alespoň 1 osobu');
      return;
    }

    if (totalPrice <= 0) {
      toast.error('Cena nemůže být 0 Kč. Zkontrolujte zadané údaje.');
      return;
    }

    const currentUser = localStorage.getItem('currentUser');
    if (!currentUser) {
      toast.error('Pro rezervaci se musíte přihlásit');
      navigate('/login');
      return;
    }

    try {
      const user = JSON.parse(currentUser);

      // Create booking first
      const bookingData = {
        user_id: user.id,
        location_id: location.id,
        location_name: location.name,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        guests: effectiveGuests,
        total_price: totalPrice,
        adults: isPerUnit ? (cabinActive ? 0 : adults) : null,
        children: isPerUnit ? (cabinActive ? 0 : children) : null,
        cars: isPerUnit ? cars : null,
        tents: isPerUnit ? tents : null,
        cabin_persons: isPerUnit && cabinActive ? cabinPersons : null,
      };

      toast.loading('Vytváření rezervace...');
      const bookingResponse = await bookingsAPI.create(bookingData);
      toast.dismiss();

      const createdBooking = bookingResponse.data;

      // If the location requires approval, do NOT go to checkout – wait for host
      if (createdBooking.status === 'pending_approval') {
        toast.success('Žádost o rezervaci odeslána majiteli ke schválení. Bude potvrzena do 24 hodin.');
        navigate('/dashboard');
        return;
      }

      // Otherwise create Stripe payment immediately
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('token');
      
      const paymentResponse = await fetch(`${API_URL}/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_id: createdBooking.id,
          origin_url: window.location.origin
        })
      });

      if (!paymentResponse.ok) {
        const errorData = await paymentResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Chyba při vytváření platby');
      }

      const paymentData = await paymentResponse.json();
      
      // Redirect to Stripe Checkout
      window.location.href = paymentData.checkout_url;
    } catch (error) {
      toast.dismiss();
      console.error('Booking error:', error);
      toast.error('Chyba při vytváření rezervace');
    }
  };

  return (
    <div 
      className="min-h-screen"
    >
      <div className="relative">
        <Header />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-3xl">Dokončit rezervaci</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Datum příjezdu</Label>
                      <Popover open={showCheckInCal} onOpenChange={setShowCheckInCal}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal mt-1"
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {checkIn ? format(checkIn, 'PPP', { locale: cs }) : 'Vyberte datum'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={checkIn}
                            onSelect={(date) => {
                              setCheckIn(date);
                              setShowCheckInCal(false);
                            }}
                            disabled={(date) => date < new Date() || isDateBusy(date)}
                            modifiers={{ busy: busyDateObjects }}
                            modifiersClassNames={{ busy: 'wc-day-busy' }}
                            initialFocus
                          />
                          <div className="px-3 pb-3 pt-1 text-xs text-gray-500 flex items-center gap-2 border-t">
                            <span className="inline-block w-3 h-3 rounded-sm bg-red-200 border border-red-300"></span>
                            Obsazené dny
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div>
                      <Label>Datum odjezdu</Label>
                      <Popover open={showCheckOutCal} onOpenChange={setShowCheckOutCal}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal mt-1"
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {checkOut ? format(checkOut, 'PPP', { locale: cs }) : 'Vyberte datum'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={checkOut}
                            onSelect={(date) => {
                              setCheckOut(date);
                              setShowCheckOutCal(false);
                            }}
                            disabled={(date) => date <= (checkIn || new Date()) || isDateBusy(date)}
                            modifiers={{ busy: busyDateObjects }}
                            modifiersClassNames={{ busy: 'wc-day-busy' }}
                            initialFocus
                          />
                          <div className="px-3 pb-3 pt-1 text-xs text-gray-500 flex items-center gap-2 border-t">
                            <span className="inline-block w-3 h-3 rounded-sm bg-red-200 border border-red-300"></span>
                            Obsazené dny
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Guests */}
                  {isPerUnit ? (
                    <div className="space-y-4 border rounded-lg p-4 bg-orange-50/40">
                      <p className="text-sm text-gray-700 font-medium">
                        Detailní ceník – zadejte počty:
                      </p>
                      {((location.price_per_cabin || 0) > 0 || (location.price_per_cabin_unit || 0) > 0) && (
                        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="cabin-option-block">
                          <Label htmlFor="bk-cabin" className="flex items-center justify-between">
                            <span className="font-semibold text-amber-900">
                              🏠 Chata / maringotka / teepee
                              <span className="text-xs font-normal text-amber-700 ml-1">
                                {(location.price_per_cabin_unit || 0) > 0 && `${location.price_per_cabin_unit} Kč/noc za objekt`}
                                {(location.price_per_cabin_unit || 0) > 0 && (location.price_per_cabin || 0) > 0 && ' + '}
                                {(location.price_per_cabin || 0) > 0 && `${location.price_per_cabin} Kč/noc/osoba`}
                              </span>
                            </span>
                          </Label>
                          <Input
                            id="bk-cabin"
                            type="number"
                            min={0}
                            className="mt-1.5"
                            value={cabinPersons}
                            onChange={(e) => setCabinPersons(Math.max(0, parseInt(e.target.value) || 0))}
                            data-testid="booking-cabin-persons-input"
                            placeholder="Počet osob v chatě"
                          />
                          <p className="text-[11px] text-amber-800 mt-1.5">
                            Pokud zvolíte chatu (počet osob &gt; 0), <strong>neúčtuje se cena za dospělého ani dítě</strong>. Auto a stan se počítají zvlášť.
                          </p>
                        </div>
                      )}
                      <div className={`grid grid-cols-2 gap-3 ${cabinActive ? 'opacity-40 pointer-events-none' : ''}`}>
                        {[
                          { key: 'adults', label: 'Dospělí', state: adults, set: setAdults, min: 1, price: location.price_per_adult, dimmable: true },
                          { key: 'children', label: 'Děti', state: children, set: setChildren, min: 0, price: location.price_per_child, dimmable: true },
                          { key: 'cars', label: 'Auta', state: cars, set: setCars, min: 0, price: location.price_per_car, dimmable: false },
                          { key: 'tents', label: 'Stany', state: tents, set: setTents, min: 0, price: location.price_per_tent, dimmable: false },
                        ].filter(({ dimmable }) => !(cabinActive && !dimmable)).map(({ key, label, state, set, min, price }) => (
                          <div key={key}>
                            <Label htmlFor={`bk-${key}`}>
                              {label}
                              {price > 0 && (
                                <span className="text-xs text-gray-500 ml-1">({price} Kč/noc/ks)</span>
                              )}
                            </Label>
                            <Input
                              id={`bk-${key}`}
                              type="number"
                              min={min}
                              className="mt-1"
                              value={state}
                              onChange={(e) => set(Math.max(min, parseInt(e.target.value) || 0))}
                              data-testid={`booking-${key}-input`}
                            />
                          </div>
                        ))}
                      </div>
                      {/* Cars & Tents shown again outside the dimmed block when cabin is active */}
                      {cabinActive && (
                        <div className="grid grid-cols-2 gap-3" data-testid="cabin-extras">
                          {[
                            { key: 'cars', label: 'Auta', state: cars, set: setCars, price: location.price_per_car },
                            { key: 'tents', label: 'Stany', state: tents, set: setTents, price: location.price_per_tent },
                          ].map(({ key, label, state, set, price }) => (
                            <div key={`extra-${key}`}>
                              <Label htmlFor={`bk-extra-${key}`}>
                                {label}
                                {price > 0 && <span className="text-xs text-gray-500 ml-1">({price} Kč/noc/ks)</span>}
                              </Label>
                              <Input
                                id={`bk-extra-${key}`}
                                type="number"
                                min={0}
                                className="mt-1"
                                value={state}
                                onChange={(e) => set(Math.max(0, parseInt(e.target.value) || 0))}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-500">
                        Limit max. osob: {maxCapacity}. Položky s cenou 0 Kč se neúčtují.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="guests">Počet osob</Label>
                      <div className="relative mt-1">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          id="guests"
                          type="number"
                          min="1"
                          max={maxCapacity}
                          className="pl-10"
                          value={guests}
                          onChange={(e) => setGuests(parseInt(e.target.value))}
                          required
                        />
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Maximální počet osob: {maxCapacity}
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 text-lg"
                    data-testid="submit-booking-btn"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    {location.instant_book === false
                      ? 'Odeslat žádost o rezervaci'
                      : 'Pokračovat k platbě'}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    {location.instant_book === false
                      ? 'Kliknutím odešlete žádost majiteli ke schválení. Bez závazku platby.'
                      : 'Kliknutím budete přesměrováni na bezpečnou platební bránu Stripe.'}
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 shadow-lg">
              <CardContent className="p-6">
                <h3 className="font-bold text-xl mb-4">Souhrn rezervace</h3>
                
                <div className="mb-6">
                  <img
                    src={location.photos?.[0] || location.image || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4'}
                    alt={location.name}
                    className="w-full h-32 object-cover rounded-lg mb-3"
                  />
                  <h4 className="font-semibold text-lg">{location.name}</h4>
                  <p className="text-sm text-gray-600">{location.type || location.category}</p>
                  {location.instant_book === false && (
                    <div className="mt-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md p-3 text-sm">
                      🔐 <b>Vyžaduje schválení.</b> Po odeslání žádosti počkejte na potvrzení majitelem (do 24 h). Platit budete až po schválení.
                    </div>
                  )}
                </div>

                <div className="space-y-3 border-t pt-4">
                  {checkIn && checkOut && (
                    isPerUnit ? (
                      <div className="space-y-1 text-sm">
                        {(cabinActive
                          ? [
                              { lbl: 'Chata (objekt)', n: 1, p: location.price_per_cabin_unit },
                              { lbl: 'Chata (osoba)', n: cabinPersons, p: location.price_per_cabin },
                              { lbl: 'Auta', n: cars, p: location.price_per_car },
                              { lbl: 'Stany', n: tents, p: location.price_per_tent },
                            ]
                          : [
                              { lbl: 'Dospělí', n: adults, p: location.price_per_adult },
                              { lbl: 'Děti', n: children, p: location.price_per_child },
                              { lbl: 'Auta', n: cars, p: location.price_per_car },
                              { lbl: 'Stany', n: tents, p: location.price_per_tent },
                            ]
                        ).filter(x => x.n > 0 && x.p > 0).map(({ lbl, n, p }) => (
                          <div key={lbl} className="flex justify-between">
                            <span className="text-gray-600">{n} × {lbl} × {nights} {nights === 1 ? 'noc' : 'nocí'}</span>
                            <span className="font-semibold">{n * p * nights} Kč</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {location.price} Kč × {nights} {nights === 1 ? 'noc' : 'nocí'}
                        </span>
                        <span className="font-semibold">{totalPrice} Kč</span>
                      </div>
                    )
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Počet osob</span>
                    <span className="font-semibold">{effectiveGuests}</span>
                  </div>
                </div>

                <div className="border-t mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg">Celkem</span>
                    <span className="font-bold text-2xl text-orange-600">
                      {totalPrice || 0} Kč
                    </span>
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Zdarma zrušení do 24h před příjezdem</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>
                      {location.instant_book === false
                        ? 'Potvrzení do 24 hodin'
                        : 'Okamžité potvrzení'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Booking;
